/**
 * Loading and failure states for the admin screens.
 *
 * Exists because of one specific blank page: a platform operator opening a
 * school's workspace. The isolation model refuses them the roster — correctly —
 * so the query 403s, `data` is undefined, and every list rendered nothing at all
 * with no explanation. A blank screen reads as a broken product; "you cannot see
 * this, and here is why" reads as a boundary working.
 */
import type { ReactNode } from 'react';
import { Card } from '@/components/ds';

export interface QueryStateProps {
  isPending: boolean;
  error: unknown;
  /** Rendered when the query succeeded but returned nothing. */
  empty?: ReactNode;
  isEmpty?: boolean;
  children?: ReactNode;
}

function messageFor(error: unknown): { title: string; detail: string } {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 403) {
    return {
      title: 'You don’t have access to this',
      detail:
        'Managing the platform does not include seeing inside a school. A school’s own admins and teachers can see this page.',
    };
  }
  if (status === 404) {
    return { title: 'Not found', detail: 'This workspace does not exist, or is suspended.' };
  }
  return {
    title: 'Could not load this',
    detail: error instanceof Error ? error.message : 'Please try again in a moment.',
  };
}

export function QueryState({ isPending, error, empty, isEmpty, children }: QueryStateProps) {
  if (isPending) return <p className="text-ink-soft">Loading…</p>;

  if (error) {
    const { title, detail } = messageFor(error);
    return (
      <Card>
        <h3 className="text-base font-bold text-forest">{title}</h3>
        <p className="mt-1 text-sm text-ink-soft">{detail}</p>
      </Card>
    );
  }

  if (isEmpty && empty) return <Card>{empty}</Card>;

  return <>{children}</>;
}
